<template>
  <div>
    <div class="service-container" style="margin-top: 8px;">
      <img class="service-main-img" src="@/assets/img/技术支持.png" alt="技术支持主图" loading="eager" />
    </div>
    <div class="calculator-container">
      <div class="header">
        <h1>肽含量计算器</h1>
        <p class="subtitle">计算肽含量百分比</p>
      </div>

      <div class="calculator-content">
        <!-- 左侧输入区域 -->
        <div class="input-section">
          <!-- 第一步：序列输入 -->
          <div class="step-section">
            <div class="step-header">
              <span class="step-number">1</span>
              <h3>输入序列</h3>
            </div>
            <div class="step-content">
              <div class="input-group">
                <label>序列（可输入氨基酸单字母或多字母，单字母为大写字母）</label>
                <el-input
                  v-model="formData.sequence"
                  type="textarea"
                  :rows="3"
                  placeholder="例如：ACDEFG 或 Ala-Cys-Asp-Glu-Phe-Gly"
                  class="sequence-input"
                ></el-input>
              </div>

              <div class="disulfide-section">
                <h4>二硫键修饰</h4>
                <el-input
                  v-model="formData.disulfide"
                  placeholder="请输入二硫键修饰信息"
                  clearable
                ></el-input>
              </div>

              <div class="cycle-section">
                <h4>成环类型</h4>
                <el-select
                  v-model="formData.cycleType"
                  placeholder="请选择成环类型"
                  class="cycle-select"
                >
                  <el-option label="未成环" value="none"></el-option>
                  <el-option label="主链成环" value="main"></el-option>
                  <el-option label="侧链成环" value="side"></el-option>
                  <el-option label="主（N端）侧（C端）成环" value="mainN_sideC"></el-option>
                  <el-option label="侧（N端）主（C端）成环" value="sideN_mainC"></el-option>
                  <el-option label="硫醚成环" value="sulfide"></el-option>
                </el-select>
              </div>

              <el-button
                type="primary"
                @click="calculateMolecularWeight"
                :loading="loadingMw"
                class="calculate-mw-btn"
                icon="el-icon-s-grid"
              >
                计算平均分子量
              </el-button>

              <!-- 分子量计算结果 -->
              <div v-if="mwResult" class="mw-result-section">
                <div class="result-item">
                  <span class="result-label">平均分子量:</span>
                  <span class="result-value highlight">{{ mwResult }} g/mol</span>
                </div>
                <div class="result-item">
                  <span class="result-label">酸性基团:</span>
                  <span class="result-value">{{ mwData.acidCount || 'N/A' }}</span>
                </div>
                <div class="result-item">
                  <span class="result-label">碱性基团:</span>
                  <span class="result-value">{{ mwData.baseCount || 'N/A' }}</span>
                </div>
              </div>

              <div v-if="warning" class="warning-section">
                <el-alert :title="warning" type="warning" show-icon></el-alert>
              </div>
            </div>
          </div>

          <!-- 第二步：肽含量计算 -->
          <div class="step-section" :class="{ 'disabled': !isValidMw }">
            <div class="step-header">
              <span class="step-number">2</span>
              <h3>计算肽含量</h3>
            </div>
            <div class="step-content">
              <div class="salt-section">
                <h4>盐类型</h4>
                <el-select
                  v-model="formData.saltType"
                  placeholder="请选择盐类型"
                  class="salt-select"
                  :disabled="!isValidMw"
                >
                  <el-option label="三氟乙酸盐(TFA)" value="TFA"></el-option>
                  <el-option label="醋酸盐(HAc)" value="HAc"></el-option>
                  <el-option label="盐酸盐(HCl)" value="HCl"></el-option>
                  <el-option label="醋酸铵盐(AA)" value="AA"></el-option>
                  <el-option label="钠盐(Na)" value="Na"></el-option>
                </el-select>
              </div>

              <div class="purity-section">
                <h4>多肽纯度</h4>
                <el-input
                  v-model="formData.purity"
                  type="number"
                  :min="0"
                  :max="100"
                  placeholder="请输入纯度百分比"
                  class="purity-input"
                  :disabled="!isValidMw"
                >
                  <template slot="append">%</template>
                </el-input>
              </div>

              <el-button
                type="success"
                @click="calculateContent"
                :loading="loadingContent"
                class="calculate-content-btn"
                icon="el-icon-s-data"
                :disabled="!isValidMw"
              >
                计算肽含量
              </el-button>
            </div>
          </div>
        </div>

        <!-- 右侧结果区域 -->
        <div class="result-section" v-if="result">
          <h3>计算结果</h3>
          <div class="result-content">
            <div class="result-item">
              <span class="result-label">平均分子量(MW):</span>
              <span class="result-value">{{ result.mw || 'N/A' }} g/mol</span>
            </div>
            <div class="result-item">
              <span class="result-label">酸基团个数:</span>
              <span class="result-value">{{ result.acidicGroups || 'N/A' }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">碱基团个数:</span>
              <span class="result-value">{{ result.basicGroups || 'N/A' }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">理论肽含量:</span>
              <span class="result-value">{{ result.theoreticalPurity || 'N/A' }}</span>
            </div>
            <div class="result-item highlight">
              <span class="result-label">目标肽含量:</span>
              <span class="result-value highlight">{{ result.targetPurity || 'N/A' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 底部信息 -->
    <div class="tk-footer">
      <div>电话：15002819872&nbsp; 邮箱：15002819872@163.com&nbsp; 地址：江西省赣州市章贡区长征大道12号</div>
      <div>备案号: <a href="https://beian.miit.gov.cn/#/Integrated/index">赣ICP备2026004521号-1</a></div>
    </div>
  </div>
</template>

<script>
import { calculatePeptide, calculatePeptideContent } from '@/api/peptide';

export default {
  name: 'PeptideContentCalculator',
  data() {
    return {
      loadingMw: false,
      loadingContent: false,
      warning: '',
      mwResult: '',
      mwData: null,
      result: null,
      formData: {
        sequence: '',
        disulfide: '',
        cycleType: 'none',
        saltType: 'TFA',
        purity: ''
      },
      cycleTypeMap: {
        'none': -1,
        'main': 0,
        'side': 1,
        'mainN_sideC': 2,
        'sideN_mainC': 3,
        'sulfide': 4
      },
      saltIdMap: {
        'TFA': 1,
        'HAc': 2,
        'HCl': 3,
        'AA': 4,
        'Na': 5
      }
    };
  },
  computed: {
    isValidMw() {
      return this.mwResult && !isNaN(parseFloat(this.mwResult)) && parseFloat(this.mwResult) > 0;
    }
  },
  methods: {
    async calculateMolecularWeight() {
      if (!this.formData.sequence.trim()) {
        this.$message.warning('请输入多肽序列');
        return;
      }

      this.loadingMw = true;
      this.warning = '';
      this.mwResult = '';
      this.mwData = null;
      this.result = null;

      try {
        const params = {
          amino: this.formData.sequence.replace(/\s+/g, '').trim()
        };

        if (this.formData.disulfide.trim()) {
          params.s2 = this.formData.disulfide.trim();
        }

        params.cycloType = this.cycleTypeMap[this.formData.cycleType];

        const response = await calculatePeptide(params);
        const data = response.data && response.data.data && response.data.data.data ? response.data.data.data : response.data;

        this.mwData = data;
        const mw = parseFloat(data.mw);

        if (isNaN(mw) || mw <= 0) {
          this.mwResult = '';
          this.mwData = null;
          this.$message.warning('计算出的分子量无效，请检查输入序列');
          return;
        }

        this.mwResult = mw.toFixed(2);

        this.$message.success('平均分子量计算成功');

      } catch (error) {
        console.error('计算失败:', error);
        if (error.response && error.response.data && error.response.data.warning) {
          this.warning = error.response.data.warning;
        } else {
          this.$message.error('计算失败，请检查输入序列');
        }
      } finally {
        this.loadingMw = false;
      }
    },
    async calculateContent() {
      if (!this.isValidMw) {
        this.$message.warning('请先计算有效的平均分子量');
        return;
      }

      if (!this.formData.purity || this.formData.purity <= 0) {
        this.$message.warning('请输入有效的多肽纯度');
        return;
      }

      this.loadingContent = true;
      this.result = null;

      try {
        const params = {
          mw: parseFloat(this.mwResult),
          acidCount: this.mwData.acidCount,
          baseCount: this.mwData.baseCount,
          saltId: this.saltIdMap[this.formData.saltType],
          purity: parseInt(this.formData.purity)
        };

        const response = await calculatePeptideContent(params);
        const data = response.data && response.data.data ? response.data.data : response.data;

        this.result = {
          mw: data.mw ? data.mw.toFixed(3) : this.mwResult,
          acidicGroups: data.acidCount,
          basicGroups: data.baseCount,
          theoreticalPurity: data.y || 'N/A',
          targetPurity: data.lastPurity || data.y || 'N/A'
        };

        this.$message.success('肽含量计算成功');

      } catch (error) {
        console.error('计算失败:', error);
        this.$message.error('计算失败，请检查输入参数');
      } finally {
        this.loadingContent = false;
      }
    }
  }
};
</script>

<style scoped>
.service-container {
  width: 100%;
  overflow: hidden;
}

.service-main-img {
  width: 100%;
  height: auto;
  max-height: 400px;
  object-fit: cover;
  display: block;
}

.calculator-container {
  max-width: 1200px;
  margin: 20px auto;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.header {
  text-align: center;
  margin-bottom: 30px;
  border-bottom: 2px solid #409EFF;
  padding-bottom: 20px;
}

.header h1 {
  color: #303133;
  margin: 0 0 10px 0;
  font-size: 28px;
}

.subtitle {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.calculator-content {
  display: flex;
  gap: 30px;
}

.input-section {
  flex: 1;
}

.result-section {
  flex: 1;
  background: #f5f7fa;
  padding: 20px;
  border-radius: 8px;
}

/* 步骤区域样式 */
.step-section {
  margin-bottom: 30px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
}

.step-section.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.step-header {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  color: #fff;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #fff;
  color: #409EFF;
  border-radius: 50%;
  font-size: 18px;
  font-weight: bold;
  margin-right: 15px;
}

.step-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.step-content {
  padding: 20px;
}

.input-group {
  margin-bottom: 20px;
}

.input-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.sequence-input {
  margin-bottom: 15px;
}

.disulfide-section,
.cycle-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f9fafc;
  border-radius: 6px;
}

.disulfide-section h4,
.cycle-section h4 {
  margin: 0 0 15px 0;
  font-size: 15px;
  color: #303133;
  font-weight: 600;
}

.cycle-select {
  width: 100%;
}

.calculate-mw-btn,
.calculate-content-btn {
  width: 100%;
  margin-bottom: 20px;
  padding: 12px;
  font-size: 16px;
}

.calculate-mw-btn {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  border: none;
}

.calculate-mw-btn:hover {
  background: linear-gradient(135deg, #66b1ff 0%, #409EFF 100%);
}

.calculate-content-btn {
  background: linear-gradient(135deg, #67C23A 0%, #95d475 100%);
  border: none;
}

.calculate-content-btn:hover {
  background: linear-gradient(135deg, #95d475 0%, #67C23A 100%);
}

.mw-result-section {
  padding: 15px;
  background: #ecf5ff;
  border-radius: 6px;
  margin-bottom: 20px;
}

.mw-result-section .result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 8px;
  border: 1px solid #d9ecff;
}

.mw-result-section .result-item:last-child {
  margin-bottom: 0;
}

.mw-result-section .result-label {
  font-weight: 500;
  color: #606266;
  min-width: 100px;
}

.mw-result-section .result-value {
  color: #303133;
  font-weight: 600;
}

.mw-result-section .result-value.highlight {
  color: #409EFF;
  font-size: 18px;
}

.warning-section {
  margin-top: 15px;
}

.salt-section,
.purity-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f9fafc;
  border-radius: 6px;
}

.salt-section h4,
.purity-section h4 {
  margin: 0 0 15px 0;
  font-size: 15px;
  color: #303133;
  font-weight: 600;
}

.salt-select,
.purity-input {
  width: 100%;
}

/* 结果区域样式 */
.result-section h3 {
  margin: 0 0 20px 0;
  color: #303133;
  border-bottom: 2px solid #409EFF;
  padding-bottom: 10px;
  font-size: 18px;
}

.result-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.result-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 15px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.3s;
  border: 1px solid #e4e7ed;
}

.result-item:hover {
  background: #e6f1fc;
  border-color: #409EFF;
  transform: translateX(3px);
}

.result-item.highlight {
  background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%);
  border: 2px solid #409EFF;
}

.result-label {
  font-weight: 500;
  color: #606266;
  flex-shrink: 0;
  min-width: 120px;
}

.result-value {
  color: #303133;
  font-weight: 600;
  text-align: right;
  word-break: break-all;
  flex: 1;
}

.result-value.highlight {
  font-size: 20px;
  color: #409EFF;
}

.tk-footer {
  background: #2f2f2f;
  color: #fff;
  text-align: center;
  padding: 30px 0 10px 0;
  margin-top: 40px;
}

.tk-footer div {
  margin: 5px 0;
  font-size: 14px;
}
.tk-footer a {
  color: #fff;
  text-decoration: none;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .service-main-img {
    max-height: 250px;
    object-fit: contain;
  }

  .tk-footer {
    padding: 20px 10px 10px 10px;
    margin-top: 20px;
    font-size: 12px;
    line-height: 1.6;
  }

  .tk-footer div {
    word-break: break-all;
    padding: 0 5px;
  }

  .calculator-container {
    margin: 10px;
    padding: 15px;
  }

  .header h1 {
    font-size: 22px;
  }

  .subtitle {
    font-size: 12px;
  }

  .calculator-content {
    flex-direction: column;
    gap: 20px;
  }

  .result-section {
    padding: 15px;
  }

  .input-group label {
    font-size: 14px;
  }

  .step-header {
    padding: 12px 15px;
  }

  .step-number {
    width: 28px;
    height: 28px;
    font-size: 16px;
    margin-right: 12px;
  }

  .step-header h3 {
    font-size: 16px;
  }

  .step-content {
    padding: 15px;
  }

  .disulfide-section h4,
  .cycle-section h4,
  .salt-section h4,
  .purity-section h4 {
    font-size: 14px;
  }

  .result-item {
    flex-direction: column;
    gap: 5px;
    padding: 10px 12px;
  }

  .result-label {
    font-size: 14px;
    min-width: auto;
  }

  .result-value {
    font-size: 15px;
    word-break: break-word;
  }

  .result-value.highlight {
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .service-main-img {
    max-height: 180px;
  }

  .header h1 {
    font-size: 18px;
  }

  .calculator-container {
    margin: 5px;
    padding: 10px;
  }

  .header {
    margin-bottom: 20px;
    padding-bottom: 15px;
  }

  .input-group label {
    font-size: 13px;
  }

  .disulfide-section,
  .cycle-section,
  .salt-section,
  .purity-section {
    padding: 12px;
  }

  .calculate-mw-btn,
  .calculate-content-btn {
    padding: 10px;
    font-size: 14px;
  }
}
</style>
